'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Fab, Paper, Slide, Fade, Box, Modal } from '@mui/material';
import { Close } from '@mui/icons-material';
import { usePathname, useRouter } from 'next/navigation';
import { useChatOpen } from './ChatContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import ChatPanel from './ChatPanel';
import ChatList from './ChatList';

const FAB_SIZE = 48;
const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 640;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 360;
const PANEL_RIGHT = 25;
const PANEL_BOTTOM = 125;
const SIZE_STORAGE_KEY = 'timmy-widget-size';

type PanelSize = { width: number; height: number };
type ResizeDir = 'n' | 'w' | 'nw';

function clampPanelSize(width: number, height: number): PanelSize {
  const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - PANEL_RIGHT - 16);
  const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - PANEL_BOTTOM - 16);
  return {
    width: Math.min(maxWidth, Math.max(MIN_WIDTH, Math.round(width))),
    height: Math.min(maxHeight, Math.max(MIN_HEIGHT, Math.round(height))),
  };
}

function readStoredSize(): PanelSize {
  if (typeof window === 'undefined') return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
  try {
    const raw = window.localStorage.getItem(SIZE_STORAGE_KEY);
    if (!raw) return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    const parsed = JSON.parse(raw);
    if (typeof parsed?.width !== 'number' || typeof parsed?.height !== 'number') {
      return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    }
    // Previous default (400×540) was never a user choice — pick up the new size.
    if (parsed.width === 400 && parsed.height === 540) {
      return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
    }
    return clampPanelSize(parsed.width, parsed.height);
  } catch {
    return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
  }
}

function persistSize(size: PanelSize) {
  try {
    window.localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(size));
  } catch {
    // ignore quota / private mode
  }
}

export default function ChatWidget() {
  const { chatOpen: open, setChatOpen: setOpen, openChatWithPrompt } = useChatOpen();
  const { user } = useCurrentUser();
  const pathname = usePathname();
  const router = useRouter();

  // FAB drag state
  const [fabPos, setFabPos] = useState<{ x: number; y: number } | null>(null);
  const [fabDragging, setFabDragging] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);
  const fabDragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, startPosX: 0, startPosY: 0 });

  // Panel resize state (desktop only). Defaults first to avoid SSR/localStorage mismatch.
  const [size, setSize] = useState<PanelSize>({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [openChat, setOpenChat] = useState<{ id: string; title: string; token: number } | null>(null);
  const [newTabToken, setNewTabToken] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resizing, setResizing] = useState(false);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const resizeRef = useRef({
    active: false,
    dir: 'nw' as ResizeDir,
    startX: 0,
    startY: 0,
    startW: DEFAULT_WIDTH,
    startH: DEFAULT_HEIGHT,
  });

  useEffect(() => {
    setSize(readStoredSize());
    const onWindowResize = () => {
      if (window.innerWidth < 600) return;
      setSize((prev) => {
        const next = clampPanelSize(prev.width, prev.height);
        if (next.width === prev.width && next.height === prev.height) return prev;
        persistSize(next);
        return next;
      });
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  // ── FAB drag handlers ────────────────────────────────────────────────────────

  const handleFabPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const rect = fabRef.current?.getBoundingClientRect();
    const startPosX = rect ? rect.left : window.innerWidth - FAB_SIZE - 25;
    const startPosY = rect ? rect.top : window.innerHeight - FAB_SIZE - 75;

    fabDragRef.current = { active: true, moved: false, startX: e.clientX, startY: e.clientY, startPosX, startPosY };
    if (!fabPos) setFabPos({ x: startPosX, y: startPosY });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleFabPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!fabDragRef.current.active) return;
    const dx = e.clientX - fabDragRef.current.startX;
    const dy = e.clientY - fabDragRef.current.startY;

    if (!fabDragRef.current.moved && Math.hypot(dx, dy) > 6) {
      fabDragRef.current.moved = true;
      setFabDragging(true);
    }
    if (!fabDragRef.current.moved) return;

    setFabPos({
      x: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, fabDragRef.current.startPosX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, fabDragRef.current.startPosY + dy)),
    });
  };

  const handleFabPointerUp = () => {
    const wasDrag = fabDragRef.current.moved;
    fabDragRef.current.active = false;
    fabDragRef.current.moved = false;
    setFabDragging(false);
    if (!wasDrag) setOpen(!open);
  };

  const handleFabPointerCancel = () => {
    fabDragRef.current.active = false;
    fabDragRef.current.moved = false;
    setFabDragging(false);
  };

  // Keep a dragged FAB on screen after rotation / window resize.
  const fabMoved = fabPos !== null;
  useEffect(() => {
    if (!fabMoved) return;
    const onWindowResize = () => {
      setFabPos((prev) => prev && {
        x: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, prev.x)),
        y: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, prev.y)),
      });
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [fabMoved]);

  // ── Panel resize handlers ────────────────────────────────────────────────────

  const handleResizePointerDown = useCallback((dir: ResizeDir) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (window.innerWidth < 600) return;
    e.preventDefault();
    e.stopPropagation();
    const { width, height } = sizeRef.current;
    resizeRef.current = {
      active: true,
      dir,
      startX: e.clientX,
      startY: e.clientY,
      startW: width,
      startH: height,
    };
    setResizing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleResizePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current.active) return;
    const { dir, startX, startY, startW, startH } = resizeRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const width = dir === 'w' || dir === 'nw' ? startW - dx : startW;
    const height = dir === 'n' || dir === 'nw' ? startH - dy : startH;
    setSize(clampPanelSize(width, height));
  }, []);

  const handleResizePointerUp = useCallback(() => {
    if (!resizeRef.current.active) return;
    resizeRef.current.active = false;
    setResizing(false);
    persistSize(sizeRef.current);
  }, []);

  // Shift+T keyboard shortcut to toggle the chat widget
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== 'T') return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) return;
      setOpen(!open);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, setOpen]);

  // Shift+F keyboard shortcut — open Timmy and pre-fill the WTB fill prompt
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'KeyF' || !e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) return;
      e.preventDefault();
      openChatWithPrompt('/fill');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openChatWithPrompt]);

  // Shift+R keyboard shortcut — open Timmy and pre-fill the /sold command
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== 'R') return;
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) return;
      e.preventDefault();
      openChatWithPrompt('/sold');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openChatWithPrompt]);

  // The standalone /timmy page is the chat itself — no floating widget there.
  // `/` only redirects (it's the PWA start page); showing the FAB there made it jump once the tab bar mounted.
  const canUseTimmy = user?.permissions?.includes('USE_TIMMY');
  if (!user || !canUseTimmy || pathname === '/timmy' || pathname === '/') return null;

  const resizeHandleSx = {
    display: { xs: 'none', sm: 'block' },
    position: 'absolute',
    zIndex: 2,
    touchAction: 'none',
    userSelect: 'none',
  } as const;

  return (
    <>
      {/* ── FAB ──────────────────────────────────────────────────────────────── */}
      <Fab
        ref={fabRef}
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerUp}
        onPointerCancel={handleFabPointerCancel}
        disableRipple={fabDragging}
        sx={{
          position: 'fixed',
          ...(fabPos ? { top: fabPos.y, left: fabPos.x, bottom: 'auto', right: 'auto' } : { bottom: { xs: 'calc(var(--tab-bar-height) + var(--chat-composer-height, 0px) + 16px)', md: 75 }, right: { xs: 16, md: 25 } }),
          zIndex: 1400,
          bgcolor: '#000',
          color: 'white',
          '&:hover': { bgcolor: '#1a1a1a' },
          width: FAB_SIZE,
          height: FAB_SIZE,
          boxShadow: fabDragging ? '0 8px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.2)',
          cursor: fabDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
          transition: 'box-shadow 0.15s ease, opacity 0.15s ease',
          '&:active': { transform: fabDragging ? 'none' : 'scale(0.94)' },
          // Hide FAB on mobile when panel is open — header close button is enough
          opacity: { xs: open ? 0 : 1, sm: 1 },
          pointerEvents: { xs: open ? 'none' : 'auto', sm: 'auto' },
        }}
      >
        <Fade in={!open} timeout={150} unmountOnExit>
          <Box
            component="img"
            src="/timmy-icon.png"
            alt="Timmy"
            draggable={false}
            sx={{ width: 32, height: 32, position: 'absolute', pointerEvents: 'none' }}
          />
        </Fade>
        <Fade in={open} timeout={150} unmountOnExit>
          <Close sx={{ fontSize: 20, position: 'absolute' }} />
        </Fade>
      </Fab>

      {/* ── Chat panel — always anchored bottom-right ─────────────────────── */}
      {/* Wrapped in a MUI Modal so Timmy sits inside the modal stack and its     */}
      {/* input is reachable even when another dialog/modal is open behind it.    */}
      <Modal
        open
        hideBackdrop
        disableScrollLock
        disableAutoFocus
        disableEnforceFocus
        keepMounted
        data-timmy-modal
        sx={{ position: 'fixed', zIndex: 1399, pointerEvents: 'none' }}
      >
      <Slide direction="up" in={open} mountOnEnter>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: { xs: 0, sm: PANEL_BOTTOM },
            right: { xs: 0, sm: PANEL_RIGHT },
            left: { xs: 0, sm: 'auto' },
            top: { xs: 0, sm: 'auto' },
            zIndex: 1399,
            width: { xs: '100%', sm: size.width },
            height: { xs: '100dvh', sm: size.height },
            maxWidth: { xs: '100%', sm: `calc(100vw - ${PANEL_RIGHT + 16}px)` },
            maxHeight: { xs: '100dvh', sm: `calc(100dvh - ${PANEL_BOTTOM + 16}px)` },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 0, sm: '12px' },
            overflow: 'hidden',
            border: { xs: 'none', sm: '1px solid' },
            borderColor: { sm: 'divider' },
            boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
            pointerEvents: open ? 'auto' : 'none',
            userSelect: resizing ? 'none' : 'auto',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <ChatPanel
              onClose={() => setOpen(false)}
              visible={open}
              onActiveChatChange={setActiveChatId}
              onChatSaved={() => setRefreshKey((key) => key + 1)}
              onOpenChats={() => setListOpen(true)}
              openChat={openChat}
              newTabToken={newTabToken}
            />
            {listOpen && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, bgcolor: 'background.paper' }}>
                <ChatList
                  selectedId={activeChatId}
                  refreshKey={refreshKey}
                  onSelect={(chat) => {
                    setOpenChat({ id: chat.id, title: chat.title, token: Date.now() });
                    setListOpen(false);
                  }}
                  onNew={() => {
                    setNewTabToken((token) => token + 1);
                    setListOpen(false);
                  }}
                  onBack={() => setListOpen(false)}
                  onClose={() => setOpen(false)}
                />
              </Box>
            )}
          </Box>

          {/* Desktop resize handles — panel is bottom-right anchored, so grow from top/left. */}
          <Box
            onPointerDown={handleResizePointerDown('n')}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            sx={{ ...resizeHandleSx, top: 0, left: 12, right: 8, height: 10, cursor: 'ns-resize' }}
          />
          <Box
            onPointerDown={handleResizePointerDown('w')}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            sx={{ ...resizeHandleSx, left: 0, top: 12, bottom: 8, width: 10, cursor: 'ew-resize' }}
          />
          <Box
            onPointerDown={handleResizePointerDown('nw')}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
            onPointerCancel={handleResizePointerUp}
            aria-label="Resize Timmy"
            sx={{ ...resizeHandleSx, top: 0, left: 0, width: 28, height: 28, cursor: 'nwse-resize' }}
          />
        </Paper>
      </Slide>
      </Modal>
    </>
  );
}
